import { useCallback, useRef, useState } from 'react';

/**
 * Sound Manager for playing notification sounds
 * Provides play, pause, stop, and retrigger capabilities
 */
class SoundManager {
  constructor() {
    this.audioCache = new Map();
    this.currentlyPlaying = new Set();
    this.enabled = true;
  }

  /**
   * Preload a sound file
   * @param {string} name - Sound identifier
   * @param {string} url - Sound file URL
   */
  preload(name, url) {
    if (!this.audioCache.has(name)) {
      const audio = new Audio(url);
      audio.preload = 'auto';
      this.audioCache.set(name, audio);
    }
  }

  /**
   * Play a sound
   * @param {string} name - Sound identifier
   * @param {Object} options - Playback options
   * @param {boolean} options.loop - Whether to loop the sound
   * @param {number} options.volume - Volume level (0-1)
   * @returns {Promise<void>}
   */
  async play(name, options = {}) {
    if (!this.enabled) return;

    const audio = this.audioCache.get(name);
    if (!audio) {
      console.warn(`Sound "${name}" not preloaded`);
      return;
    }

    const { loop = false, volume = 1.0 } = options;

    // Stop if already playing (retrigger behavior)
    this.stop(name);

    // Create new audio instance for simultaneous playback
    const playAudio = audio.cloneNode();
    playAudio.loop = loop;
    playAudio.volume = volume;

    this.currentlyPlaying.add(name);

    playAudio.addEventListener('ended', () => {
      this.currentlyPlaying.delete(name);
    });

    playAudio.addEventListener('error', () => {
      this.currentlyPlaying.delete(name);
    });

    try {
      await playAudio.play();
    } catch (error) {
      // Audio play failed (likely due to user interaction requirement)
      this.currentlyPlaying.delete(name);
    }
  }

  /**
   * Pause a currently playing sound
   * @param {string} name - Sound identifier
   */
  pause(name) {
    const audio = this.audioCache.get(name);
    if (audio) {
      audio.pause();
    }
  }

  /**
   * Stop a sound (pause and reset to beginning)
   * @param {string} name - Sound identifier
   */
  stop(name) {
    const audio = this.audioCache.get(name);
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    this.currentlyPlaying.delete(name);
  }

  /**
   * Stop all currently playing sounds
   */
  stopAll() {
    this.audioCache.forEach((audio, name) => {
      audio.pause();
      audio.currentTime = 0;
    });
    this.currentlyPlaying.clear();
  }

  /**
   * Retrigger a sound (stop and play again)
   * @param {string} name - Sound identifier
   * @param {Object} options - Playback options
   */
  async retrigger(name, options = {}) {
    this.stop(name);
    await this.play(name, options);
  }

  /**
   * Check if a sound is currently playing
   * @param {string} name - Sound identifier
   * @returns {boolean}
   */
  isPlaying(name) {
    return this.currentlyPlaying.has(name);
  }

  /**
   * Enable or disable sounds
   * @param {boolean} enabled
   */
  setEnabled(enabled) {
    this.enabled = enabled;
    if (!enabled) {
      this.stopAll();
    }
  }

  /**
   * Check if sounds are enabled
   * @returns {boolean}
   */
  isEnabled() {
    return this.enabled;
  }

  /**
   * Play a synthesized notification tone using Web Audio API (no file required)
   * @param {number} frequency - Hz (default 880)
   * @param {number} duration - seconds (default 0.12)
   * @param {number} volume - 0-1 (default 0.25)
   * @param {string} type - oscillator type (default 'sine')
   */
  playTone(frequency = 880, duration = 0.12, volume = 0.25, type = 'sine') {
    if (!this.enabled) return;
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      const ctx = new AudioContext();
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);
      gain.gain.setValueAtTime(volume, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration);
      oscillator.onended = () => ctx.close();
    } catch (_) {}
  }
}

// Singleton instance
const soundManager = new SoundManager();

/**
 * React hook for using the sound manager
 */
export const useSoundManager = () => {
  const [enabled, setEnabledState] = useState(soundManager.isEnabled());

  const play = useCallback(async (name, options) => {
    await soundManager.play(name, options);
  }, []);

  const pause = useCallback((name) => {
    soundManager.pause(name);
  }, []);

  const stop = useCallback((name) => {
    soundManager.stop(name);
  }, []);

  const stopAll = useCallback(() => {
    soundManager.stopAll();
  }, []);

  const retrigger = useCallback(async (name, options) => {
    await soundManager.retrigger(name, options);
  }, []);

  const preload = useCallback((name, url) => {
    soundManager.preload(name, url);
  }, []);

  const isPlaying = useCallback((name) => {
    return soundManager.isPlaying(name);
  }, []);

  const setEnabled = useCallback((value) => {
    soundManager.setEnabled(value);
    setEnabledState(value);
  }, []);

  return {
    play,
    pause,
    stop,
    stopAll,
    retrigger,
    preload,
    isPlaying,
    enabled,
    setEnabled,
  };
};

export default soundManager;
